import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import type { Deal, EvidenceEntry, FinancialYear, FlexibleLineItem, Treatment } from '@/api/sharedTypes'
import { HttpErrorCatalog } from '@/utils/httpErrors'
import { recentAction } from './clickTrail'

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '') + '/api'

// API ROUTE NAMESPACE CONVENTION
// All apiClient calls use paths relative to baseURL (VITE_API_BASE_URL + '/api').
// Example: apiClient.get('/qoe/deals') → GET {BASE}/api/qoe/deals
//
// Route groups:
//   /auth/*          → qoe_auth.py (password login, token refresh, logout)
//   /qoe/*           → routers/qoe/ (deals, normalization, seller/buyer portals)
//   /v1/admin/*      → routers/admin/ (users, access-requests)
//   /v1/documents/*  → document intake endpoints
//   /api/v1/*        → legacy endpoints still on old router (use sparingly)
//
// Never call the raw axios instance directly — always use apiClient so
// baseURL + withCredentials are applied consistently.

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
  // C-08: auth tokens live in httpOnly cookies, not localStorage — the browser
  // must be told to send/store them even though the API is a different origin
  // from the SWA-hosted frontend.
  withCredentials: true,
})

// E2E test mode support: inject test token into Authorization header
apiClient.interceptors.request.use((config) => {
  const testToken = localStorage.getItem('playwright_test_token')
  const isE2EMode = localStorage.getItem('playwright_e2e_mode') === 'true'
  if (testToken && isE2EMode) {
    config.headers.Authorization = `Bearer ${testToken}`
  }

  // AI-CALL-AUDIT-1C R2: which screen and which button, for the ai.call audit
  // row. Client-reported labels — never used for access control (§3 fence).
  config.headers['X-Yansar-Screen'] = (window.location.pathname + window.location.search).slice(0, 200)
  const a = recentAction()
  if (a) config.headers['X-Yansar-Action'] = a

  return config
})

// Silent refresh state
let _isRefreshing = false
let _pendingRequests: Array<() => void> = []

// Response interceptor — 401 triggers one silent refresh attempt before redirecting.
// Attaches a human-readable `translatedMessage` to every AxiosError so components
// can display it directly without re-parsing the error envelope.
apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError & { translatedMessage?: string }) => {
    // Attach translated message — works for both HTTP errors and network errors
    error.translatedMessage = HttpErrorCatalog.fromAnyError(error)

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    const url = originalRequest?.url ?? ''

    // The refresh call must never re-enter the refresh branch. It carries no
    // _retry flag, so a 401 on it would hit the `_isRefreshing` guard below and
    // park itself in _pendingRequests — waiting on the very refresh that is
    // awaiting it. That deadlocks: the outer await never returns, `finally`
    // never runs, and _isRefreshing stays true for the life of the tab, hanging
    // every subsequent 401 too.
    const isRefreshCall = url.includes('/auth/refresh')

    // Seller/buyer portals authenticate with the ?token= URL, not a session
    // cookie (CLAUDE.md: "Token auth for sellers/buyers"). There is no refresh
    // token to spend, so a 401 there is final — reject straight through so the
    // portal renders Access Denied instead of hanging on its loading card.
    const isPortalTokenAuth = url.includes('token=')

    // The request URL alone is not enough. AuthContext fires GET /v1/me on every
    // page load, portals included, and that call carries no token= param. On a
    // public route it 401s by design; letting it reach the branch below spends a
    // refresh that also 401s and lands in the catch, which sets
    // window.location.href = '/login'. On /login itself that reload re-mounts
    // AuthContext and repeats — an infinite navigation loop. On /seller and
    // /buyer it evicts a correctly-authenticated token holder off their portal.
    // Neither route has a session to refresh, so neither belongs in this branch.
    const PUBLIC_ROUTES = [
      '/login', '/forgot-password', '/reset-password', '/request-access',
      '/seller', '/buyer', '/privacy', '/data-retention', '/terms', '/handoff',
    ]
    const path = typeof window !== 'undefined' ? window.location.pathname : ''
    const isPublicRoute = PUBLIC_ROUTES.some(r => path === r || path.startsWith(r + '/'))

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isRefreshCall &&
      !isPortalTokenAuth &&
      !isPublicRoute
    ) {
      originalRequest._retry = true
      if (_isRefreshing) {
        return new Promise<unknown>(resolve => {
          _pendingRequests.push(() => resolve(apiClient(originalRequest)))
        })
      }
      _isRefreshing = true
      try {
        // No body needed — refresh token comes from the httpOnly qoe_refresh_token
        // cookie. No localStorage write needed — the server sets the new
        // qoe_access_token cookie directly on this response.
        await apiClient.post('/auth/refresh')
        _pendingRequests.forEach(cb => cb())
        _pendingRequests = []
        return apiClient(originalRequest)
      } catch {
        clearAuthToken()
        // Belt-and-braces: assigning location.href to the page you are already on
        // is a full reload, so an unguarded redirect here can only ever loop.
        if (path !== '/login') window.location.href = '/login'
        return Promise.reject(error)
      } finally {
        _isRefreshing = false
      }
    }
    return Promise.reject(error)
  }

)

export const clearAuthToken = () => {
  localStorage.removeItem('qoe_user')
}

export interface RawFinding {
  finding_id: string
  deal_id: string
  number: number
  section: string
  title: string
  body: string
  is_essentials: boolean
  score: 'PASS' | 'REVIEW' | 'FLAG' | 'PENDING'
  score_note: string | null
  narrative: string | null
  evidence: EvidenceEntry[]
  /** FINDINGS-COUNT-1: true only when this check actually ran against the
   *  deal's data (a real score was recorded, or evidence was persisted).
   *  Optional so an older backend degrades to "nothing has fired". */
  fired?: boolean
  /** FINDINGS-DOMAIN-1: why this finding has no score. All optional so an
   *  older backend degrades to "everything is in scope, just pending". */
  state?: 'assessed' | 'not_in_scope' | 'data_missing' | 'unscored'
  in_scope?: boolean
  required_inputs?: string
}

export async function getDeal(dealId: string): Promise<Deal> {
  const res = await apiClient.get<Deal>(`/qoe/deals/${dealId}`)
  return res.data
}

/** GET /qoe/deals/{id}/normalization — the wire shape, verified against
 *  apps/api/routers/qoe/deals/normalization.py.
 *
 *  QOE-CPA-FIXPACK-1 D2: this used to be typed `{session_id, deal_id, data}`.
 *  There is no `data` key on the wire — every field is top-level — so every
 *  caller reading `.data` got `undefined` and concluded the deal had no
 *  financials no matter how many years were entered and approved.
 */
/** ENG-AI-1 — one figure the narrative asserted that the computed numbers do
 *  not support. `message` is pre-rendered server-side so the CPA reads the same
 *  sentence the approve route would refuse on. */
export interface NarrativeGuardFlag {
  asserted: string
  asserted_value: number
  kind: string
  reason: string
  closest_fact: string | null
  closest_value: number | null
  message: string
}

/** NARRATIVE-GUARD-1 verdict. `passed: false` means the draft cannot be
 *  approved — the server enforces that; the UI only explains it. */
export interface NarrativeGuard {
  passed: boolean
  checked_count: number
  flags: NarrativeGuardFlag[]
}

export interface DealNormalization {
  session_id: string
  deal_id: string
  financial_years: FinancialYear[]
  adjustments: unknown[]
  ai_narrative: string | null
  /** ENG-AI-1 — the PROSE gate, distinct from `status` below. 'none' | 'draft'
   *  | 'approved'. Only 'approved' renders the narrative into the report. */
  narrative_status?: string | null
  /** The stored NARRATIVE-GUARD-1 verdict for the current draft. Shape is
   *  core.qoe.narrative_types.GuardResult.to_dict(); null before a draft. */
  narrative_guard?: NarrativeGuard | null
  narrative_generated_by?: string | null
  narrative_approved_by?: string | null
  narrative_approved_at?: string | null
  /** REPORT gate — 'draft' until a CPA approves the statement; 'approved' after.
   *  Independent of narrative_status: neither implies the other. */
  status: string
  approved_by: string | null
  approved_at: string | null
  form_extras_json: Record<string, unknown>
  flexible_items: unknown[]
}

export async function getDealNormalization(dealId: string): Promise<DealNormalization> {
  const res = await apiClient.get(`/qoe/deals/${dealId}/normalization`)
  return res.data as DealNormalization
}

/** GET /qoe/deals/{id}/findings — the wire shape, verified against
 *  apps/api/qoe_deals.py. The scope fields are what make a Standard deal
 *  read as 54 rather than 74 (QOE-CPA-FIXPACK-1 D1/D4); they were previously
 *  dropped at the type boundary, so no caller could see them.
 */
export interface DealFindingsResponse {
  findings: RawFinding[]
  /** Findings that have actually been evaluated against this deal (FINDINGS-COUNT-1).
   *  This is what the UI counts — never the catalogue size. */
  count: number
  /** Total checks in the catalogue. Label it separately; it is not a result. */
  catalogue_count?: number
  /** The deal's resolved scope depth: 'lite' | 'focused' | 'full'. */
  scope_tier?: string
  /** How many of the catalogue are in scope for THIS deal — 17 / 54 / 74,
   *  already adjusted for any per-deal negotiated delta. */
  in_scope_count?: number
  out_of_scope_count?: number
  scope_delta?: Record<string, unknown> | null
  sections?: Array<Record<string, unknown>>
}

export async function getDealFindings(dealId: string): Promise<DealFindingsResponse> {
  const res = await apiClient.get(`/qoe/deals/${dealId}/findings`)
  return res.data as DealFindingsResponse
}

/** GET /qoe/deals/{id}/journey — QOE-TRUTH-AND-JOURNEY-1 R9-R11. Every field
 *  here is server-derived from the same signal the real action routes gate
 *  on (core/qoe/deal_journey.py) — this is not a second, frontend-typed
 *  opinion about what "done" means for a stage.
 */
export interface DealJourneyGate {
  key: string
  label: string
  met: boolean
  /** Present on gates with a countable state, e.g. documents: "3 of 8". */
  detail?: string
}

export interface DealJourney {
  /** FORWARD_STATUS_ORDER, verbatim — never hand-typed on the frontend. */
  stages: string[]
  current: string
  completed: string[]
  /** The gate(s) blocking the hop out of `current` — not a cumulative list
   *  of every gate ever satisfied. */
  gates: DealJourneyGate[]
  next_action: { label: string } | null
}

export async function getDealJourney(dealId: string): Promise<DealJourney> {
  const res = await apiClient.get(`/qoe/deals/${dealId}/journey`)
  return res.data as DealJourney
}

export async function getDealReport(dealId: string): Promise<{
  url: string | null
  generated_at: string | null
  filename: string | null
}> {
  try {
    const res = await apiClient.get(`/qoe/deals/${dealId}/report`)
    return res.data as { url: string | null; generated_at: string | null; filename: string | null }
  } catch (err: unknown) {
    if ((err as { response?: { status: number } }).response?.status === 404) {
      return { url: null, generated_at: null, filename: null }
    }
    throw err
  }
}

export interface FindingScorePatch {
  score: 'pass' | 'review' | 'flag' | 'pending'
  score_note?: string
  narrative?: string
  approved_by?: string
}

export async function patchFindingScore(
  dealId: string,
  findingNumber: number,
  payload: FindingScorePatch,
): Promise<void> {
  await apiClient.patch(`/qoe/deals/${dealId}/findings/${findingNumber}`, payload)
}

// ── Multi-Entity Consolidation (ENG-8) ───────────────────────────────────────

/** CONSOL-FIX-1 C-B — `pro_rata` is RETIRED. Proportionate consolidation is an
 *  IFRS joint-arrangement concept, not a US GAAP option for a controlled sub;
 *  the backend 422s on it. Control is `full` (100% of every line + NCI),
 *  significant influence is `equity` (one line), passive is `cost`. */
export type ConsolidationMethod = 'full' | 'equity' | 'cost' | 'auto'

export interface DealEntity {
  entity_id: string
  deal_id: string
  name: string
  entity_type: string
  ownership_pct: number
  consolidation_method: ConsolidationMethod
  is_primary: boolean
  normalization_session_id: string | null
  notes: string | null
  /** FIELDS-1 P2 — entity is OUTSIDE the deal perimeter. Intercompany
   *  transactions with it are flagged as real ongoing costs, not washed out. */
  carved_out?: boolean
  /** CONSOL-FIX-1 C-A — the two plain-language ASC 810 control facts. Either
   *  one true consolidates the entity regardless of the voting interest. */
  guarantees_debt?: boolean
  exists_to_serve_group?: boolean
  /** Parent-subsidiary link. Drives the statement LABEL only — "Consolidated"
   *  for a parent-sub group, "Combined" for brother-sister common control. */
  parent_entity_id?: string | null
}

/** CONSOL-FIX-1 P0 — the safety stamp the backend attaches to every
 *  consolidated output. Wording comes from core/ingestion/consolidation_gate.py;
 *  never retype it here, or screen and artifact can drift. */
export interface PreliminaryStamp {
  status: string
  not_for_client_issuance: boolean
  label: string
  method_note: string
  decision_gate: string
  report_issuance_blocked: boolean
}

/** CONSOL-FIX-1 C-C — an ASC 850 transaction with a RETAINED affiliate. These
 *  are disclosed, never eliminated: the buyer keeps paying them after close. */
export interface RelatedPartyDisclosure {
  step: number
  entity_a: string
  entity_b: string
  description: string
  period: string
  amount: number
  note: string
  standard: string
}

/** QOE-RECON-1 §3a/§3b — one entity's contribution to a consolidated period,
 *  AFTER the ownership/method treatment the engine applied — never the
 *  entity's standalone reported figure. `revenue`/`ebitda`/`owner_comp_total`
 *  are `null`, never `0`, when the method genuinely produces no such line
 *  (equity method has no revenue line; passive/cost contributes nothing).
 *  An explicit `0` means the entity SHOULD have contributed and didn't —
 *  see `note` — never render that case as absent. */
export interface EntityContribution {
  entity_id: string
  entity_name: string
  consolidation_method: string
  revenue: number | null
  ebitda: number | null
  owner_comp_total: number | null
  note: string | null
}

/** QOE-RECON-1 §3c/§3d — the explicit tie: subtotal (from
 *  `entity_contributions`) minus what was actually eliminated, vs. the
 *  consolidated figure the engine produced independently. `foots` is the
 *  single boolean the screen must gate on — a false value is a loud, blocking
 *  failure state, never a silent disagreement. no-mint: a subtotal or
 *  expected value with nothing to sum is `null`, never `0`. */
export interface ConsolidationTie {
  subtotal_revenue: number | null
  subtotal_ebitda: number | null
  eliminated_revenue: number | null
  eliminated_ebitda: number | null
  held_for_review: number | null
  expected_revenue: number | null
  expected_ebitda: number | null
  revenue_foots: boolean
  ebitda_foots: boolean
  foots: boolean
  revenue_diff: number | null
  ebitda_diff: number | null
}

export interface ConsolidatedPeriod {
  period_label: string
  fiscal_year: number
  /** CONSOL-FIX-1 C-E — "Consolidated" (parent-sub) or "Combined"
   *  (brother-sister common control). Not decoration: it is what the
   *  statement is allowed to be called. */
  statement_label?: string
  revenue: number
  /** null means "no entity reported it" — distinct from zero. */
  cogs?: number | null
  gross_profit?: number | null
  operating_expenses?: number | null
  net_income?: number
  ebitda: number
  nci_adjustment: number
  adjusted_ebitda_consolidated: number
  /** C-B — EBITDA after backing out the minority owners' share. */
  ebitda_attributable_to_parent?: number
  /** DECISION-1 — owner benefit defaults to SDE, not adjusted EBITDA. */
  owner_comp_total?: number
  sde_consolidated?: number
  /** C-A/C-B — which rung of the ASC 810 ladder each entity landed on. */
  method_by_entity?: Record<string, string>
  method_rationale?: Array<Record<string, unknown>>
  equity_method_income?: number
  equity_method_entities?: string[]
  entities_included: string[]
  /** C-C — affiliates outside the deal perimeter. */
  retained_entities?: string[]
  related_party_disclosures?: RelatedPartyDisclosure[]
  elimination_count: number
  review_count: number
  warnings: string[]
  /** CONSOL-FIX-1 P0 — true while consolidation logic is under CPA review. */
  preliminary?: boolean
  method_note?: string
  /** QOE-RECON-1 §3a/§3b — per-entity contribution as actually taken into
   *  this total. Never the standalone figure. */
  entity_contributions?: EntityContribution[]
  /** QOE-RECON-1 §3c/§3d — the tie the screen must show its work for. */
  tie?: ConsolidationTie
}

/** QOE-RECON-1 §5 — one itemized intercompany elimination. Amounts on both
 *  sides, not just a count: counts are not evidence, amounts are. */
export interface EliminationItem {
  step: number
  description: string
  period: string
  entity_a: string
  entity_b: string
  amount_a: number
  amount_b: number
  amount: number
  severity: string
  auto_eliminate: boolean
  elimination_kind: string
  in_perimeter: boolean
  ebitda_impact: number
}

export interface ConsolidationResult {
  deal_id: string
  consolidated_periods: ConsolidatedPeriod[]
  qoe_finding: Record<string, unknown>
  /** FIELDS-1 P2 — which entities the run treated as outside the perimeter. */
  carved_out_entity_ids?: string[]
  /** CONSOL-FIX-1 P0 — not-for-issuance caveat carried by every result. */
  preliminary_stamp?: PreliminaryStamp
}

export interface DealEntityList {
  entities: DealEntity[]
  /** CONSOL-FIX-1 P0 — present so the page can stamp itself before a run. */
  preliminary_stamp?: PreliminaryStamp
}

export async function listDealEntities(dealId: string): Promise<DealEntityList> {
  const res = await apiClient.get<{
    deal_id: string
    entities: DealEntity[]
    preliminary_stamp?: PreliminaryStamp
  }>(`/qoe/deals/${dealId}/entities`)
  return {
    entities: res.data.entities,
    preliminary_stamp: res.data.preliminary_stamp,
  }
}

export async function createDealEntity(
  dealId: string,
  payload: {
    name: string
    entity_type?: string
    ownership_pct?: number
    consolidation_method?: ConsolidationMethod
    is_primary?: boolean
    notes?: string
    carved_out?: boolean
    guarantees_debt?: boolean
    exists_to_serve_group?: boolean
    parent_entity_id?: string | null
  },
): Promise<DealEntity> {
  const res = await apiClient.post<DealEntity>(`/qoe/deals/${dealId}/entities`, payload)
  return res.data
}

export async function deleteDealEntity(dealId: string, entityId: string): Promise<void> {
  await apiClient.delete(`/qoe/deals/${dealId}/entities/${entityId}`)
}

export async function runConsolidation(dealId: string): Promise<ConsolidationResult> {
  const res = await apiClient.post<ConsolidationResult>(`/qoe/deals/${dealId}/consolidate`)
  return res.data
}

// ── eSignature ────────────────────────────────────────────────────────────────

export interface SignatureRequest {
  id: string
  document_type: string
  deal_id: string | null
  engagement_id: string | null
  status: string
  signing_url: string | null
  requested_at: string
  signed_at: string | null
  signed_pdf_blob_url: string | null
}

// ── Flexible line items (FIELDS-2) ───────────────────────────────────────────

/** What a deal's custom line items contribute, split by destination. */
export interface FlexibleItemRouting {
  revenue_delta: number
  opex_delta: number
  add_back_total: number
  owner_benefit_total: number
  debt_like_total: number
  memo_count: number
  /** Items counted nowhere because they have no valid treatment yet. */
  needs_treatment: Array<Record<string, unknown>>
}

export interface FlexibleItemsResponse {
  deal_id: string
  items: FlexibleLineItem[]
  /** treatment -> plain-language meaning, straight from core/. */
  treatments: Record<string, string>
  routing: FlexibleItemRouting
}

export async function listFlexibleItems(dealId: string): Promise<FlexibleItemsResponse> {
  const res = await apiClient.get<FlexibleItemsResponse>(
    `/qoe/deals/${dealId}/flexible-items`,
  )
  return res.data
}

/**
 * Add a custom line item. `treatment` is required — the API returns 422 without
 * one, because an item nobody has classified cannot be counted anywhere.
 */
export async function createFlexibleItem(
  dealId: string,
  payload: {
    label: string
    amount?: number
    year?: number | null
    treatment: Treatment
    note?: string | null
  },
): Promise<FlexibleLineItem> {
  const res = await apiClient.post<FlexibleLineItem>(
    `/qoe/deals/${dealId}/flexible-items`,
    payload,
  )
  return res.data
}

/**
 * Edit an item, or assign a treatment to an extracted one. Omitted fields are
 * left as stored, so this doubles as the "accept this extracted line" call.
 */
export async function updateFlexibleItem(
  dealId: string,
  itemId: string,
  payload: {
    label?: string
    amount?: number
    year?: number | null
    treatment?: Treatment
    note?: string | null
  },
): Promise<FlexibleLineItem> {
  const res = await apiClient.patch<FlexibleLineItem>(
    `/qoe/deals/${dealId}/flexible-items/${itemId}`,
    payload,
  )
  return res.data
}

export async function deleteFlexibleItem(dealId: string, itemId: string): Promise<void> {
  await apiClient.delete(`/qoe/deals/${dealId}/flexible-items/${itemId}`)
}

